import Footer from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import Hero from "@/components/marketing/Hero";
import { Badge } from "@/components/ui/badge";

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <Navigation showAuth={true} className="mt-4" />

      <Hero
        badge="Privacy"
        headline="Privacy Policy"
        description="How we handle your data at Arcitext"
      />

      <div className="pb-16">
        <div className="prose prose-slate max-w-3xl mx-auto">
          <p className="text-text-muted">
            Last updated: November 4, 2025
          </p>

          {/* 1. Introduction */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">1</span>
            Introduction
          </h2>
          <p>
            At Arcitext, we value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or purchase our products.
          </p>
          <p>
            We keep data collection to a minimum and only collect what is necessary to provide you with our products and services. We do not sell your personal data to third parties.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We only collect minimal data necessary to provide our services" },
                { text: "We never sell your personal data to third parties" },
                { text: "We use trusted third-party services for email and payment processing" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 2. Who We Are */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">2</span>
            Who We Are
          </h2>
          <p>
            FjordKit Digital is the data controller responsible for your personal data. We are committed to ensuring that your privacy is protected.
          </p>
          <p>
            <strong>Company details:</strong>
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Legal name:</strong> FjordKit Digital</li>
            <li><strong>Business address:</strong> Vissevej 121, 9210 Aalborg SØ, Denmark</li>
            <li><strong>Email:</strong> <a href="mailto:hello@arcitext.com" className="text-accent-indigo hover:text-accent-indigo/80">hello@arcitext.com</a></li>
          </ul>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "FjordKit Digital is the data controller for your personal data" },
                { text: "We're based in Denmark and subject to EU data protection laws" },
                { text: "You can contact us at hello@arcitext.com with any privacy questions" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 3. What Data We Collect */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">3</span>
            What Data We Collect
          </h2>
          <p>
            We collect and process minimal personal data. Here's what we collect and why:
          </p>
          <h3 className="text-lg font-semibold mt-4">When you visit our website:</h3>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Technical data:</strong> We use Plausible Analytics which collects anonymous data like page views, referrer sources, and device type</li>
            <li><strong>Usage data:</strong> How you interact with our website, including pages visited and time spent, but without personally identifying information</li>
          </ul>

          <h3 className="text-lg font-semibold mt-4">When you purchase our products:</h3>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Contact information:</strong> Email address for order confirmation and delivery</li>
            <li><strong>Payment information:</strong> Processed securely through our payment processor (Polar)</li>
            <li><strong>Purchase history:</strong> What products you've purchased and when</li>
          </ul>

          <h3 className="text-lg font-semibold mt-4">When you subscribe to our newsletter:</h3>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Email address:</strong> To send you updates and marketing communications</li>
            <li><strong>Name (optional):</strong> To personalize our communications</li>
          </ul>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We collect minimal data - mainly email and privacy-friendly analytics" },
                { text: "Our analytics (Plausible) doesn't track individuals or use cookies" },
                { text: "Payment details are processed by Polar, not stored by Arcitext" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 4. How We Use Your Data */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">4</span>
            How We Use Your Data
          </h2>
          <p>
            We use your data for the following purposes:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>To provide our products and services:</strong> Delivering digital products, processing payments, and sending order confirmations</li>
            <li><strong>To improve our website and products:</strong> Analyzing usage patterns to enhance user experience</li>
            <li><strong>To communicate with you:</strong> Responding to inquiries and providing customer support</li>
            <li><strong>To send marketing communications:</strong> Sharing updates about new products or features (only with your consent)</li>
            <li><strong>To comply with legal obligations:</strong> Maintaining records for tax and accounting purposes</li>
          </ul>
          <p>
            When you connect RSS feeds or upload content into Arcitext, we process that material solely to provide the requested functionality. You are responsible for ensuring you have the necessary rights to any feeds or content you add, and Arcitext is not liable for any unauthorized or unlicensed material that you choose to surface through the platform.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We only use your data for specific, legitimate purposes" },
                { text: "Marketing emails are only sent with your explicit consent" },
                { text: "You can opt out of marketing communications at any time" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 5. Third-Party Services */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">5</span>
            Third-Party Services
          </h2>
          <p>
            We use trusted third-party services to help us operate our business. These services may process your personal data on our behalf:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>MailerSend / MailerLite:</strong> For email newsletter management, marketing communications and transactional emails such as magic link-emails.</li>
            <li><strong>Polar:</strong> For secure payment processing</li>
            <li><strong>Supabase:</strong> For secure database and authentication</li>
            <li><strong>OpenAI:</strong> For AI-copywriting assistance and analysis</li>
            <li><strong>Plausible Analytics:</strong> A privacy-friendly analytics tool that helps us understand how visitors use our website without collecting personal data or using cookies</li>
          </ul>
          <p>
            These third parties have access to your personal data only to perform specific tasks on our behalf and are obligated to maintain its confidentiality. They each have their own privacy policies that govern how they handle your data.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We use MailerSend / MailerLite for email, Polar for payments, and Plausible for analytics, Supabase for infrastructure, OpenAI for AI functionality" },
                { text: "Plausible is a privacy-friendly analytics tool that doesn't use cookies" },
                { text: "All third-party services we use comply with data protection laws" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 6. Data Security */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">6</span>
            Data Security
          </h2>
          <p>
            We implement appropriate security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li>Secure HTTPS connections for all website traffic</li>
            <li>Regular security assessments and updates</li>
            <li>Limited access to personal data within our organization</li>
            <li>Use of reputable third-party services with strong security practices</li>
          </ul>
          <p>
            While we strive to protect your personal data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to implementing reasonable security practices.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We use HTTPS encryption to protect data transmitted on our website" },
                { text: "We limit access to personal data within our organization" },
                { text: "We regularly review and update our security practices" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 7. Your Rights */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">7</span>
            Your Rights
          </h2>
          <p>
            Under data protection laws, you have rights regarding your personal data. These include:
          </p>
          <ul className="list-disc list-outside ml-6 my-6">
            <li><strong>Right to access:</strong> You can request a copy of your personal data</li>
            <li><strong>Right to rectification:</strong> You can ask us to correct inaccurate or incomplete data</li>
            <li><strong>Right to erasure:</strong> You can ask us to delete your personal data in certain circumstances</li>
            <li><strong>Right to restrict processing:</strong> You can ask us to limit how we use your data</li>
            <li><strong>Right to data portability:</strong> You can request a copy of your data in a machine-readable format</li>
            <li><strong>Right to object:</strong> You can object to our processing of your data in certain circumstances</li>
            <li><strong>Right to withdraw consent:</strong> You can withdraw consent for data processing at any time</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at <a href="mailto:hello@Arcitext.com" className="text-accent-indigo hover:text-accent-indigo/80">hello@Arcitext.com</a>. We will respond to your request within 30 days.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "You have several rights regarding your personal data" },
                { text: "You can request access, correction, or deletion of your data" },
                { text: "Contact hello@Arcitext.com to exercise your rights" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 8. Cookies */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">8</span>
            Cookies
          </h2>
          <p>
            Our website uses minimal cookies to enhance your experience. Cookies are small text files stored on your device that can help analyze website traffic and customize content.
          </p>
          <p>
            We primarily use essential cookies necessary for the website to function properly. For analytics, we use Plausible, which is a privacy-friendly analytics tool that doesn't use cookies and doesn't collect personal data.
          </p>
          <p>
            You can control cookies through your browser settings. Disabling cookies may affect some functionality of our website.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We use minimal cookies for essential website functions only" },
                { text: "Our analytics (Plausible) doesn't use cookies or collect personal data" },
                { text: "You can disable cookies through your browser settings" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 9. Changes to This Policy */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">9</span>
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will post the updated policy on this page with a revised "Last updated" date.
          </p>
          <p>
            We encourage you to review this Privacy Policy periodically to stay informed about how we protect your personal data. Your continued use of our website after any changes indicates your acceptance of the updated policy.
          </p>
          <div className="my-8">
            <h4 className="font-semibold mb-2">What you should know:</h4>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { text: "We may update this policy to reflect changes in our practices" },
                { text: "Updates will be posted on this page with a revised date" },
                { text: "Continued use after changes means you accept the updated policy" }
              ].map(({ text }) => (
                <Badge key={text} className="text-sm" variant="glass">
                  {text}
                </Badge>
              ))}
            </div>
          </div>

          {/* 10. Contact */}
          <h2 className="mt-8 mb-4 font-serif font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white font-semibold text-sm">10</span>
            Contact
          </h2>
          <p>
            If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at
            <a href="mailto:hello@arcitext.com" className="text-primary hover:text-primary/80"> hello@arcitext.com</a>.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
